import { Timestamp } from 'firebase/firestore';
import { CalendarDays, ChevronRight, Clock, MapPin } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { getDistance, getLocalizedLangValue } from 'src/helpers';
import { useEventPlaces } from 'src/hooks';
import { TranslationType } from 'src/types';
import { parseFirestoreTimestamp } from 'src/utils';

import {
  BottomContainer,
  Button,
  CardWrapper,
  Description,
  FreePlaces,
  Image,
  ImageWrapper,
  LabelContainer,
  Logo,
  PriceLabel,
  PriceLabelText,
  RedLabelContainer,
  RedLabelText,
  Time,
  TimeContainer,
  Title,
  TitleWrapper,
} from './Card.styled';

export type CardDataProps = {
  image: string;
  title: string;
  description?: string;
  action?: () => void;
  time?: string;
  date?: string;
  isSmall?: boolean;
  isVertical?: boolean;
  hotelGeopoint?: {
    latitude: number;
    longitude: number;
  };
  attractionGeopoint?: {
    latitude: number;
    longitude: number;
  };
  logo?: string;
  specialOfferLabel?: string | TranslationType;
  priceLabel?: string;
  freeAccess?: boolean;
  remainingEntries?: string;
  endDate?: string | Timestamp;
  isMicro?: boolean;
  isSpecialOffer?: boolean;
};

export const Card: FC<CardDataProps> = (props) => {
  const {
    image,
    title,
    date,
    hotelGeopoint,
    attractionGeopoint,
    description,
    action, time, isSmall,
    isVertical,
    logo,
    specialOfferLabel,
    priceLabel,
    freeAccess,
    remainingEntries,
    endDate,
    isMicro,
    isSpecialOffer,
  } = props;
  const { i18n } = useTranslation();
  const distance = hotelGeopoint && attractionGeopoint
    ? getDistance(
      {
        latitude: hotelGeopoint.latitude,
        longitude: hotelGeopoint.longitude,
      },
      {
        latitude: attractionGeopoint?.latitude,
        longitude: attractionGeopoint?.longitude,
      },
    )
    : 0;

  const calculatedDistance = distance > 1000 ? `${(distance / 1000).toFixed(1)} km` : `${Math.round(distance)} m`;

  const { places } = useEventPlaces({
    isOpen: freeAccess,
    remainingEntries,
  });

  const isEndDateString = typeof endDate === 'string';
  const specialOfferEndDate = isEndDateString ? endDate : parseFirestoreTimestamp(endDate)?.date;
  const sepcialOfferLabelText = typeof specialOfferLabel === 'string'
    ? specialOfferLabel : specialOfferLabel
    && getLocalizedLangValue(specialOfferLabel as TranslationType, i18n.language);
  return (
    <CardWrapper onClick={action} data-testid="card" isSmall={isSmall}>
      <ImageWrapper isMicro={isMicro} isVertical={isVertical} isSpecialOffer={isSpecialOffer}>
        {!!image && <Image src={image} alt="card" isMicro={isMicro} />}
        {!!logo && <Logo src={logo} alt="card" />}
        {places() !== null && <FreePlaces>{places()}</FreePlaces>}
        {!!priceLabel && <PriceLabel><PriceLabelText>od {priceLabel} zł</PriceLabelText></PriceLabel>}
      </ImageWrapper>
      <BottomContainer>
        <TitleWrapper>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
          <LabelContainer>
            {specialOfferLabel && (
            <RedLabelContainer>
              <RedLabelText>{sepcialOfferLabelText}</RedLabelText>
            </RedLabelContainer>
            )}
            {attractionGeopoint && calculatedDistance && (
            <TimeContainer>
              <MapPin size={16} color="#757575" />
              <Time>{calculatedDistance}</Time>
            </TimeContainer>
            )}
            {date && (
            <TimeContainer>
              <CalendarDays size={16} color="#757575" />
              <Time>{date}</Time>
            </TimeContainer>
            )}
            {time && (
              <TimeContainer>
                <Clock size={16} color="#757575" />
                <Time>{time}</Time>
              </TimeContainer>
            )}
          </LabelContainer>
          {specialOfferEndDate && (
            <TimeContainer>
              <Time>Oferta ważna do: {specialOfferEndDate}</Time>
            </TimeContainer>
          )}
        </TitleWrapper>
        <Button>
          <ChevronRight size={22} color="#C18244" />
        </Button>
      </BottomContainer>
    </CardWrapper>
  );
};
